import * as React from "react";
import {useEffect, useState} from "react";
import {
  Checkbox,
  TextField,
  Autocomplete,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Box, Popper,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
// import "../CSS/components/SearchableMultiSelect.css";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface CustomSelectProps {
  items: { value: string | number; label: string }[];
  inputLabel?: string;
  selectAllOption?: boolean;
  onChange?: (event: any, value: (string | number)[]) => void;
  value: { value: string | number; label: string }[];
  setValue;
  selectAllInitially? : boolean;
  closeOnSelection? : boolean;
  sx?;
  disabled? : boolean;
  hasInitialized? : boolean;
  setHasInitialized?: (initialized: boolean) => void;
}

const greenCheckBoxSX = {
  '&.Mui-checked': {
    color: 'var(--naturGreen)', // Set color when checkbox is checked
  },
  '&:hover': {
    borderColor: 'var(--naturGreen)', // Set border color on hover
  }
};

const customPopper = (props) => {
  return (
    <Popper {...props}
            placement="bottom"
            sx={{ height: "10px", }}
            style={{ width: props.anchorEl.clientWidth, height: "5px" }} />
  )
};


const SearchableMultiSelect: React.FC<CustomSelectProps> = ({
  items,
  inputLabel = "",
  selectAllOption = true,
  selectAllInitially = false,
  value ,  // State lifted from parent
  setValue, // Function to update state in the parent
  sx = {},
  closeOnSelection = false,
  disabled = false,
  hasInitialized, setHasInitialized
}) => {
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const isAllSelected = value.length === items.length;

  // If selectAllInitially is true, select all items when the component mounts
  // useEffect(() => {
  //   if (selectAllInitially && !hasInitialized) {
  //     setSelectAll(true);
  //     setValue(items);
  //     setHasInitialized(true);
  //   }
  // }, [selectAllInitially, items, hasInitialized]);
  const internalInitialized = React.useRef(false);
  useEffect(() => {
    if (!selectAllInitially) return;

    // Only run the effect if there are items and nothing is selected yet.
    if (value.length > 0 || items.length === 0) return;

    if (typeof hasInitialized === "boolean" && typeof setHasInitialized === "function") {
      if (!hasInitialized) {
        setSelectAll(true);
        setValue(items);
        setHasInitialized(true);
      }
    } else {
      if (!internalInitialized.current) {
        setSelectAll(true);
        setValue(items);
        internalInitialized.current = true;
      }
    }
  }, [
    selectAllInitially,
    items,
    value,
    hasInitialized,
    setHasInitialized,
    setValue,
  ]);

  const handleToggleSelectAll = () => {
    if (disabled) return;
    const newSelectedValues = selectAll ? [] : items;
    setValue(newSelectedValues);
    setSelectAll(!selectAll);
  };

  return (
    <Autocomplete
      multiple
      options={items}
      value={value}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onChange={(_e, newValue, reason) => {
        if (reason === "clear" || reason === "removeOption") {setSelectAll(false);}
        if (reason === "selectOption" && value.length === items.length-1) {setSelectAll(true);}
        setValue(newValue);
        // onChange?.(_e, newValue);
      }}
      disableCloseOnSelect={!closeOnSelection}
      sx={{
        '& .MuiInputLabel-root': {
          color: 'var(--font-primary) !important', // Color of the label
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--naturGreen)', // Green border
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--naturGreen)', // Green border on hover
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--naturGreen)', // Green border on focus
        },
        opacity: disabled ? 0.6 : 1, // <-- Add opacity to indicate disabled state
        pointerEvents: disabled ? "none" : "auto", // <-- Prevent interactions when disabled
        ...sx
      }}
      getOptionLabel={(option) => option.label}
      renderOption={(props, option, { selected }) => {
        // const optionStyle = {
        //   backgroundColor: hoveredItem === option.value ? 'var(--primary-color)'  // Change color on hover
        //     : selected
        //     ? 'transparent'  // Change color when selected
        //     : 'transparent',  // Default color when not hovered or selected
        //   cursor: 'pointer', // Change cursor to pointer on hover
        // };

        return (<li {...props}
                    key={option.value}
                    style={{
                      transition: 'background-color 0.15s ease-in-out',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--primary-color)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{marginRight: 10, marginLeft: 10}}
            sx={greenCheckBoxSX}
            checked={selected}
          />
          {option.label}
        </li>);
      }}
      style={{width: 500}}
      renderInput={(params) => <TextField {...params} label={inputLabel}/>}
      renderTags={(value, getTagProps) =>
        isAllSelected
          ? ["Alle ausgewählt"]
          : value.map((option, index) => (
            <Chip {...getTagProps({index})} label={option.label} key={option.value} />
            ))
      }
      PaperComponent={(paperProps) => (
        <Paper {...paperProps}>
          {selectAllOption && (
            <>
              <Box onMouseDown={(e) => e.preventDefault()} pl={1.5} py={0.5} sx={{
                transition: 'background-color 0.15s ease-in-out',
                '&:hover': {
                  backgroundColor: 'var(--primary-color)',  // Apply primary color on hover
                },
              }}
            >
                <FormControlLabel
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggleSelectAll();
                  }}
                  label="Alle auswählen"
                  control={<Checkbox icon={icon}
                                     checkedIcon={checkedIcon}
                                     checked={selectAll}
                                     style={{ marginRight: 10, marginLeft: 25 }}
                                     sx={greenCheckBoxSX}
                  />}
                />
              </Box>
              <Divider />
            </>
          )}
          {paperProps.children}
        </Paper>
      )}
      PopperComponent={customPopper}
    />
  );
};

export default SearchableMultiSelect;